import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiCheck,
  FiAward,
  FiTrendingUp,
  FiShield,
  FiUsers,
  FiCheckCircle,
  FiPhone,
  FiMail,
  FiMapPin,
  FiZap,
  FiTarget,
} from 'react-icons/fi';
import {
  GiChemicalDrop,
  GiFactory,
  GiMechanicalArm,
  GiWheat,
  GiOilDrum,
  GiDrill,
  GiCarWheel,
  GiWindTurbine,
} from 'react-icons/gi';
import { BsDroplet, BsRecycle, BsShieldCheck } from 'react-icons/bs';
import banner1 from '../assets/banner2.png';
import banner2 from '../assets/banner1.png';
import banner3 from '../assets/banner3.png';
import ContactFormModal from '../components/ContactFormModal';

const Home = () => {
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const observerRef = useRef(null);

  const products = [
    {
      name: 'RU DSA-6',
      tagline: 'The Eco-Friendly De-Scaling Agent',
      benefit: 'Removes 100% of internal scales, boosting engine equipment life by up to 50% without demounting',
      icon: '⚙️',
      color: 'from-vp-blue-600 to-vp-blue-500',
      accentColor: 'from-vp-blue-400 to-vp-blue-300',
      path: '/products/ru-dsa-6',
      image: banner1,
    },
    {
      name: 'RU ORA-7',
      tagline: 'Powerful 3-in-1 Industrial Solution',
      benefit: 'Non-flammable, water-based cleaner that tackles oil, rust, and carbon, replacing hazardous fossil fuels',
      icon: '🛡️',
      color: 'from-vp-grey-700 to-vp-grey-600',
      accentColor: 'from-vp-grey-500 to-vp-grey-400',
      path: '/products/ru-ora-7',
      image: banner2,
    },
    {
      name: 'V-MUKTI 24',
      tagline: 'Water Base Parthenium Solution',
      benefit: 'The only water base solution for safe and effective eradication of Parthenium Hysterophorus',
      icon: '🌿',
      color: 'from-green-600 to-emerald-600',
      accentColor: 'from-green-400 to-emerald-400',
      path: '/products/v-mukti-24',
      image: banner3,
    },
  ];

  const industries = [
    { name: 'Energy Industry', icon: GiWindTurbine, color: 'from-vp-blue-600 to-vp-blue-500', desc: 'High-performance solutions for power generation and distribution' },
    { name: 'Defense', icon: BsShieldCheck, color: 'from-vp-grey-700 to-vp-grey-600', desc: 'Specialized water base solutions for defense equipment and operations' },
    { name: 'Oil & Petroleum', icon: GiOilDrum, color: 'from-vp-grey-800 to-vp-grey-700', desc: 'High-performance solutions for extraction and refining' },
    { name: 'Agriculture', icon: GiWheat, color: 'from-vp-green-600 to-vp-green-500', desc: 'Water base weed control and crop protection' },
  ];

  const stats = [
    { label: 'Industries Served', value: '10+', icon: GiFactory, color: 'text-vp-blue-600' },
    { label: 'Equipment Life Improved', value: '50%', icon: FiTrendingUp, color: 'text-vp-green-600' },
    { label: 'Eco-Safe Products', value: '100%', icon: BsRecycle, color: 'text-vp-green-600' },
  ];

  const commitments = [
    {
      title: 'Premium Quality',
      desc: 'Supplying high-quality, biodegradable water base solution efficiently and responsibly',
      icon: FiAward,
      gradient: 'from-vp-green-600 to-vp-green-500',
    },
    {
      title: 'Eco-Safe',
      desc: 'Non-corrosive, non-flammable, and harmless to humans, plants, and the environment',
      icon: BsRecycle,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Proven Results',
      desc: 'Solutions engineered to enhance equipment life and improve operational efficiency',
      icon: FiTrendingUp,
      gradient: 'from-blue-500 to-cyan-500',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProductIndex((prev) => (prev + 1) % products.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [products.length]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Molecular structure SVG component
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <circle cx="180" cy="120" r="3" fill="currentColor" opacity="0.25" />
          <circle cx="60" cy="160" r="3.5" fill="currentColor" opacity="0.35" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
          <line x1="80" y1="40" x2="140" y2="70" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
          <line x1="140" y1="70" x2="180" y2="120" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
          <line x1="20" y1="20" x2="60" y2="160" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-pattern)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-vp-cream-50">
      {/* Hero Section with Chemical Theme */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Rotating Banner Images Background */}
        <div className="absolute inset-0">
          {products.map((product, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentProductIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {/* Dark overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70" />
            </div>
          ))}
        </div>

        {/* Molecular Pattern Overlay */}
        <div className="absolute inset-0 text-vp-green-500 opacity-10">
          <MolecularPattern />
        </div>

        {/* Floating Chemical Molecules */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              initial={{ 
                x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920), 
                y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
                scale: 0 
              }}
              animate={{ 
                y: [null, Math.random() * -100 - 50],
                x: [null, Math.random() * 100 - 50],
                scale: [0, 1, 1, 0],
                rotate: [0, 360]
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "easeInOut"
              }}
            >
              <div className="relative">
                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-vp-green-400/30 to-vp-blue-500/30 blur-sm" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-vp-green-600 rounded-full" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Hexagonal shapes (chemical structure inspired) */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`hex-${i}`}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                rotate: [0, 360],
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <svg width="80" height="80" viewBox="0 0 100 100" className="text-vp-green-500/15">
                <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </motion.div>
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Floating Badge with Chemical Drop */}
            <motion.div 
              className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-xl px-6 py-3 rounded-full text-vp-grey-800 mb-8 border border-vp-green-500/30 shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0] 
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: "easeInOut" 
                }}
              >
                <GiChemicalDrop className="text-vp-blue-600 text-2xl" />
              </motion.div>
              <span className="font-semibold text-sm md:text-base text-black">
                20+ Years of Water Base Solution Innovation Excellence
              </span>
            </motion.div>

            {/* Main Headline with Animated Gradient */}
            <motion.h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <span 
                className="block text-white drop-shadow-2xl"
              >
                Water Base Solution Innovation Excellence
              </span>
            </motion.h1>

            {/* Sub-headline with typing effect visual */}
            <motion.p 
              className="text-xl md:text-2xl lg:text-3xl text-white mb-12 max-w-5xl mx-auto leading-relaxed font-light drop-shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
            >
              Eco-Friendly, Water-Based, Biodegradable Solutions
              <br className="hidden md:block" />
              <span className="text-vp-green-300 font-semibold">For Industrial plants, Agriculture and Petro-chemicals</span>
            </motion.p>

            {/* CTA Buttons with Hover Effects */}
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/products"
                className="group relative px-10 py-5 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-bold text-lg rounded-full overflow-hidden transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-vp-blue-600 to-vp-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center space-x-3">
                  <FiZap className="text-xl" />
                  <span>Explore Our Products</span>
                  <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
                </span>
              </Link>
              <button
                onClick={() => setContactModalOpen(true)}
                className="group px-10 py-5 bg-white backdrop-blur-md text-vp-grey-800 font-bold text-lg rounded-full border-2 border-vp-grey-300 hover:bg-vp-grey-800 hover:text-white hover:border-vp-grey-800 transition-all duration-300 shadow-lg flex items-center space-x-3"
              >
                <FiTarget className="text-xl" />
                <span>Contact Our Experts</span>
              </button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div 
              className="mt-16 flex flex-wrap items-center justify-center gap-8 text-lg font-bold text-white drop-shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-vp-green-300 text-xl" />
                <span>ISO Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-vp-green-300 text-xl" />
                <span>100% Eco-Safe</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-vp-green-300 text-xl" />
                <span>Biodegradable</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-vp-green-300 text-xl" />
                <span>Made in India</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Animated Scroll Indicator */}
        
      </section>

      {/* Animated Stats Section with Chemical Flask Design */}
      <section className="py-20 bg-vp-cream-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-vp-cream-100 via-white to-vp-cream-100" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <span className="inline-block px-6 py-3 bg-gradient-to-r from-vp-green-100 to-vp-blue-100 rounded-full text-vp-green-700 font-semibold mb-6 border border-vp-green-300">
                Why Choose VP Chem Technologies
              </span>
            </motion.div>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-vp-grey-900">
              Trusted by Industries
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600 mt-2">
                Proven Excellence
              </span>
            </h2>
            
            <p className="text-lg md:text-xl text-vp-grey-700 max-w-3xl mx-auto leading-relaxed">
              Delivering sustainable, high-performance water base solutions that drive measurable results
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="relative group"
              >
                {/* Hexagon background */}
                <div className="absolute inset-0 transform scale-95 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-green-100">
                    <polygon points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5" fill="currentColor" />
                  </svg>
                </div>
                
                <div className="relative text-center p-8 rounded-2xl bg-white shadow-lg border border-vp-cream-300 group-hover:border-vp-green-400 transition-all duration-500">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: index * 0.5 }}
                  >
                    <stat.icon className={`w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 ${stat.color}`} />
                  </motion.div>
                  <div className="text-5xl md:text-6xl font-black mb-2">
                    <span className="bg-gradient-to-r from-vp-green-600 to-vp-blue-600 bg-clip-text text-transparent">
                      {stat.value}
                    </span>
                  </div>
                  <div className="text-sm md:text-base text-black font-semibold">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section with Glassmorphism */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-vp-grey-700 via-vp-grey-600 to-vp-grey-700 text-white relative overflow-hidden bg-pattern-hexagon">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-vp-green-500/10 rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-vp-blue-500/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-vp-blue-400/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <span className="inline-block px-6 py-2 bg-white/10 backdrop-blur-xl rounded-full text-vp-green-400 font-semibold mb-6 border border-vp-green-400/30">
                  Our Commitment to Excellence
                </span>
              </motion.div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Driving Global Performance
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-400 via-vp-blue-400 to-vp-green-400">
                  Grounded in Sustainability
                </span>
              </h2>
              
              <div className="flex justify-center mb-8">
                <div className="w-24 h-1.5 bg-gradient-to-r from-vp-green-500 via-vp-blue-500 to-vp-green-500 rounded-full" />
              </div>
              
              <p className="text-xl md:text-2xl text-black leading-relaxed max-w-4xl mx-auto">
                Welcome to <span className="font-bold text-black">VP Chem Technologies LLP</span>. We deliver premium-grade, 
                biodegradable water base solutions backed by industry expertise and innovative manufacturing excellence.
              </p>
            </div>

            {/* Mission Statement Card */}
            <motion.div 
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-2xl border border-white/20 rounded-3xl p-10 md:p-12 mb-16 shadow-xl relative overflow-hidden group"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-vp-green-500/10 to-vp-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-center space-x-4 text-center">
                <GiChemicalDrop className="text-5xl text-vp-green-400 animate-pulse-slow hidden md:block" />
                <p className="text-xl md:text-2xl text-black leading-relaxed">
                  Our mission: <span className="font-bold text-vp-green-600">Maximize equipment life</span> and{' '}
                  <span className="font-bold text-vp-blue-600">minimize ecological impact</span> through 
                  high-performance, water-based chemistry.
                </p>
                <BsRecycle className="text-5xl text-vp-blue-400 animate-spin-slow hidden md:block" />
              </div>
            </motion.div>

            {/* Commitment Cards */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {commitments.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  whileHover={{ y: -10 }}
                  className="group relative"
                >
                  {/* Glowing background effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full hover:bg-white/10 hover:border-white/30 transition-all duration-500">
                    {/* Icon with animated background */}
                    <motion.div 
                      className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <item.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h4 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-white to-vp-grey-200 bg-clip-text text-black">
                      {item.title}
                    </h4>
                    <p className="text-black leading-relaxed text-lg">
                      {item.desc}
                    </p>
                    
                    {/* Animated arrow */}
                    <motion.div 
                      className="mt-6 flex items-center text-vp-green-400 font-semibold"
                      initial={{ x: 0 }}
                      whileHover={{ x: 10 }}
                    >
                      <FiArrowRight className="text-xl" />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div 
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/about"
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-vp-grey-900 font-bold text-lg rounded-full hover:bg-gradient-to-r hover:from-vp-green-500 hover:to-vp-blue-500 hover:text-white transform hover:scale-105 transition-all duration-300 shadow-xl"
              >
                <span>Read Our Vision & Mission</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Products Carousel with 3D Effects */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-vp-cream-50 via-white to-vp-cream-100 relative overflow-hidden bg-pattern-dots">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-vp-green-100/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-vp-blue-100/20 to-transparent rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <span className="inline-block px-6 py-2 bg-gradient-to-r from-vp-green-100 to-vp-blue-100 rounded-full text-vp-green-700 font-semibold mb-6 shadow-md">
                🧪 Our Flagship Solutions
              </span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-vp-grey-900 mb-6">
              Innovation in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600">
                Every Drop
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-black max-w-3xl mx-auto">
              Breakthrough water base solution engineered for maximum performance
            </p>
          </motion.div>

          {/* Product Cards Carousel */}
          <div className="relative max-w-7xl mx-auto">
            <div className="relative h-[650px] md:h-[550px]">
              {products.map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
                  animate={{
                    opacity: index === currentProductIndex ? 1 : 0,
                    scale: index === currentProductIndex ? 1 : 0.8,
                    x: index === currentProductIndex ? 0 : index < currentProductIndex ? -100 : 100,
                    rotateY: index === currentProductIndex ? 0 : index < currentProductIndex ? -20 : 20,
                    zIndex: index === currentProductIndex ? 10 : 0,
                  }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="absolute inset-0"
                >
                  <div className="group bg-white rounded-3xl shadow-xl hover:shadow-2xl overflow-hidden h-full border-2 border-vp-cream-300 hover:border-vp-green-400 transition-all duration-500">
                    <div className="grid md:grid-cols-2 h-full">
                      {/* Image Side */}
                      <div className="relative overflow-hidden bg-gradient-to-br from-vp-grey-50 to-white flex items-center justify-center p-4">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Content Side */}
                      <div className="p-8 md:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-vp-cream-100 relative overflow-hidden">
                        {/* Decorative element */}
                        <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl ${product.accentColor} opacity-5 rounded-full blur-3xl`} />
                        
                        <div className="relative z-10">
                          <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            <h3 className="text-5xl font-black text-vp-grey-900 mb-3">
                              {product.name}
                            </h3>
                            <h4 className="text-2xl md:text-3xl font-black text-vp-grey-800 mb-6 leading-tight">
                              {product.tagline}
                            </h4>
                          </motion.div>
                          
                          <motion.div 
                            className={`bg-gradient-to-r ${product.accentColor} bg-opacity-10 border-l-4 border-vp-green-600 p-6 rounded-r-2xl mb-8 shadow-md`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            <div className="flex items-start space-x-4">
                              <FiCheckCircle className="text-vp-green-600 text-3xl mt-1 flex-shrink-0" />
                              <p className="text-lg text-vp-grey-800 font-medium leading-relaxed">
                                {product.benefit}
                              </p>
                            </div>
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                          >
                            <Link
                              to={product.path}
                              className={`group inline-flex items-center justify-center space-x-3 px-10 py-5 bg-gradient-to-r ${product.color} text-white font-bold text-lg rounded-2xl hover:shadow-2xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300`}
                            >
                              <span>Discover More</span>
                              <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
                            </Link>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Navigation Dots with Animation */}
            <div className="flex justify-center space-x-3 mt-8">
              {products.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentProductIndex(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentProductIndex
                      ? 'w-12 h-4 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 shadow-md'
                      : 'w-4 h-4 bg-vp-cream-300 hover:bg-vp-grey-400'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={`Go to product ${index + 1}`}
                />
              ))}
            </div>

            {/* Arrow Navigation with Animated Hover */}
            <motion.button
              onClick={() => setCurrentProductIndex((prev) => (prev === 0 ? products.length - 1 : prev - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-14 h-14 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-vp-green-50 transition-colors hidden md:flex group"
              whileHover={{ scale: 1.1, x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiArrowRight className="w-7 h-7 text-black rotate-180 group-hover:text-vp-green-600" />
            </motion.button>
            <motion.button
              onClick={() => setCurrentProductIndex((prev) => (prev === products.length - 1 ? 0 : prev + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-14 h-14 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-vp-green-50 transition-colors hidden md:flex group"
              whileHover={{ scale: 1.1, x: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiArrowRight className="w-7 h-7 text-black group-hover:text-vp-green-600" />
            </motion.button>
          </div>

          {/* CTA */}
          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/products"
              className="group inline-flex items-center space-x-2 text-vp-green-600 font-bold text-xl hover:text-vp-green-700 transition-colors"
            >
              <span>View All Products</span>
              <FiArrowRight className="group-hover:translate-x-2 transition-transform text-2xl" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Industries Section with Hover Effects */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-vp-grey-700 via-vp-grey-600 to-vp-grey-700 relative overflow-hidden bg-pattern-grid">
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(0, 135, 81, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(0, 135, 81, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span 
              className="inline-block px-6 py-2 bg-white/10 backdrop-blur-xl rounded-full text-vp-green-400 font-semibold mb-6 border border-vp-green-400/30"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring" }}
            >
              🏭 Diverse Industry Applications
            </motion.span>
            
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Industries We{' '}
              <span className="bg-gradient-to-r from-vp-green-600 to-vp-blue-600 bg-clip-text text-transparent">
                Empower
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-black max-w-3xl mx-auto">
              Powering performance across sectors with innovative water base solution
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -15, scale: 1.03 }}
                className="group relative"
              >
                {/* Glowing effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-vp-green-500/20 to-vp-blue-500/20 rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                
                <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:bg-white/10 hover:border-vp-green-400/50 transition-all duration-500 h-full">
                  {/* Icon with rotation animation */}
                  <motion.div
                    className="text-6xl mb-6"
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.6 }}
                  >
                    <industry.icon className="w-16 h-16 text-vp-green-400 group-hover:text-vp-blue-400 transition-colors" />
                  </motion.div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-black mb-4 group-hover:text-vp-green-600 transition-colors">
                    {industry.name}
                  </h3>
                  <p className="text-black leading-relaxed mb-6 text-lg">
                    {industry.desc}
                  </p>
                  
                  <motion.div 
                    className="flex items-center text-vp-green-400 font-semibold"
                    initial={{ x: 0 }}
                    whileHover={{ x: 10 }}
                  >
                    <span className="text-sm">Explore Solutions</span>
                    <FiArrowRight className="ml-2" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <Link
              to="/industries"
              className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-vp-grey-900 font-bold text-lg rounded-2xl hover:bg-vp-green-500 hover:text-white transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 shadow-xl"
            >
              <span>See All 10+ Industries</span>
              <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section with Particle Effect */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-vp-green-600 via-vp-blue-600 to-vp-green-600 relative overflow-hidden">
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        {/* Animated gradient blobs */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div 
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <span className="inline-block px-6 py-2 bg-white/20 backdrop-blur-xl rounded-full text-white font-semibold mb-8 border border-white/30">
                ✨ Let's Work Together
              </span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-8 leading-tight">
              Ready to Transform Your
              <br className="hidden md:block" />
              <motion.span 
                className="relative inline-block"
                whileHover={{ scale: 1.05 }}
              >
                Operations?
                <motion.div 
                  className="absolute -bottom-3 left-0 right-0 h-2 bg-white/50 rounded-full"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                />
              </motion.span>
            </h2>
            
            <p className="text-xl md:text-2xl text-black mb-12 max-w-3xl mx-auto leading-relaxed">
              Get in touch with our experts to find the perfect water base solution tailored to your industry needs
            </p>

            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link
                to="/contact"
                className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-12 py-6 bg-white text-vp-green-600 font-bold text-xl rounded-2xl hover:bg-vp-cream-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-xl"
              >
                <motion.div whileHover={{ rotate: 15 }}>
                  <FiPhone className="text-2xl" />
                </motion.div>
                <span>Contact Us Today</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <Link
                to="/products"
                className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-12 py-6 bg-transparent border-3 border-white text-white font-bold text-xl rounded-2xl hover:bg-white hover:text-green-600 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300"
              >
                <span>Browse Products</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>

            {/* Contact Details */}
            <motion.div 
              className="mt-20 pt-12 border-t border-white/30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <div className="grid md:grid-cols-3 gap-8 text-white">
                {[
                  { icon: FiPhone, label: 'Call Us', value: 'Sanjay Vyas +91 9371640163', href: 'tel:+919371640163' },
                  { icon: FiMail, label: 'Email Us', value: 'info@vpchemtech.com', href: 'mailto:info@vpchemtech.com' },
                  { icon: FiMapPin, label: 'Visit Us', value: 'Ahmedabad, Gujarat, India', href: '#' },
                ].map((contact, index) => (
                  <motion.div
                    key={index}
                    className="group"
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                    >
                      <contact.icon className="text-4xl mb-3 mx-auto group-hover:text-green-200 transition-colors" />
                    </motion.div>
                    <div className="font-bold text-lg mb-2">{contact.label}</div>
                    <a 
                      href={contact.href} 
                      className="text-white hover:text-white transition-colors text-lg"
                    >
                      {contact.value}
                    </a>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
};

export default Home;



